<template>
  <section v-if="hasChapters" class="chapters" data-testid="viewer-chapters">
    <h2 class="sr-only">Chapters</h2>

    <button href="#" class="prev-btn" data-testid="viewer-chapters-prev-btn" @click="scrollLeft" :disabled="isAtStart">
      <Icon name="general.prevItem" size="base" />
      <span class="sr-only">Scroll timeline left</span>
    </button>
    <button href="#" class="next-btn" data-testid="viewer-chapters-next-btn" @click="scrollRight" :disabled="isAtEnd">
      <Icon name="general.nextItem" size="base" />
      <span class="sr-only">Scroll timeline right</span>
    </button>
    <ul class="chapters-list" ref="containerRef">
      <li v-for="(item, index) in chapters" :key="index" class="chapter-item" data-testid="viewer-chapter-item">
        <div class="timestamp-with-share">
          <span class="z-20">
            <ButtonClipboard v-bind="getChapterShareConfig(item)" data-testid="viewer-chapter-link" />
          </span>
          <span @click.stop.prevent="itemStore.setVideoTime(item.startTime)">{{ formatTimeCode(item.timecode) }}</span>
        </div>
        <a @click.stop.prevent="itemStore.setVideoTime(item.startTime)" href="#" class="chapter-title" data-testid="viewer-chapter-title">{{ item.title }}</a>
      </li>
    </ul>
  </section>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { Icon } from '@/storybook';

import { useAiMetadataStore } from '../store/useAiMetadataStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { TrackEvents } from '@/composables/useTrack';
import ButtonClipboard from './meta_buttons/ButtonClipboard.vue';

const itemStore = useViewerItemStore();
const aiMetadataStore = useAiMetadataStore();
const { hasChapters, chapters } = storeToRefs(aiMetadataStore);

const containerRef = ref<HTMLUListElement | null>(null);
const isAtStart = ref(true);
const isAtEnd = ref(false);

const formatTimeCode = (timecode?: string) => {
  if (timecode && timecode.startsWith('00:')) {
    return timecode.slice(3);
  }
  return timecode;
};

// Share button configuration
const getChapterShareConfig = (chapter: any) => ({
  icon: 'general.copy',
  tooltip: 'Copy chapter link',
  dataTestId: `chapter-share-${chapter.startTime}`,
  content: itemStore.getTimestampedShareLink(chapter.startTime),
  successMessage: 'Chapter link copied to clipboard',
  trackingEvent: TrackEvents.VIEWER_CHAPTER_SHARE,
  buttonClass: 'no-hover-bg',
});

const getScrollAmount = () => {
  if (!containerRef.value || !containerRef.value.parentElement) return 0;
  const containerWidth = containerRef.value.parentElement.offsetWidth;
  return containerWidth * 0.8;
};

const scrollLeft = () => {
  if (!containerRef.value) return;
  containerRef.value.scrollBy({
    left: -getScrollAmount(),
    behavior: 'smooth',
  });
};

const scrollRight = () => {
  if (!containerRef.value) return;
  containerRef.value.scrollBy({
    left: getScrollAmount(),
    behavior: 'smooth',
  });
};

const checkScrollPosition = () => {
  if (!containerRef.value) return;
  const ul = containerRef.value;
  const maxScrollLeft = ul.scrollWidth - ul.clientWidth;

  isAtStart.value = ul.scrollLeft <= 0;
  isAtEnd.value = ul.scrollLeft >= maxScrollLeft - 1;
};

onMounted(() => {
  if (!containerRef.value) return;

  checkScrollPosition();
  containerRef.value.addEventListener('scroll', checkScrollPosition);
});
</script>
<style scoped>
.chapters {
  @apply m-4 p-2.5 bg-white dark:bg-zinc-700 rounded-10 relative;
}
.prev-btn,
.next-btn {
  @apply absolute top-1/2 -translate-y-1/2 z-20 w-8 h-8 flex items-center justify-center bg-white dark:bg-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-300 rounded-full shadow-md;
}
.next-btn {
  @apply right-2.5;
}
.chapters-list {
  @apply flex gap-4 overflow-x-auto flex-nowrap;
}
.chapters-list::-webkit-scrollbar {
  @apply hidden;
}
.chapters-list::after {
  @apply content-[''] absolute top-[1.25rem] bg-slate-200 dark:bg-zinc-600 left-2 right-2 h-1 rounded z-0;
}
.chapter-item {
  @apply relative z-10 shrink-0 flex flex-col gap-1 items-start;
}
.timestamp-with-share {
  @apply flex items-center gap-1 text-xs font-semibold text-black dark:text-white bg-slate-300 dark:bg-zinc-500 p-1 rounded-md;
}
.chapter-title {
  @apply text-base max-w-[168px] dark:text-zinc-300 hover:text-blue-500 dark:hover:text-blue-500;
}
.chapter-title::after {
  @apply content-[''] absolute inset-0;
}
button:disabled {
  display: none;
}
</style>
